import { useState } from 'react';
import { Loader2, AlertCircle, RotateCcw } from 'lucide-react';
import {
  SITE_CONTENT_DEFAULTS,
  type SiteContentKey,
  type SiteContentRow,
  type SiteContentEntry,
} from '@pms/shared';
import { useSiteContent, useUpsertSiteContent } from '@/hooks/api/useSiteContent';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { showToast } from '@/lib/toast';
import { extractErrorMessage } from '@/lib/error';

const VALUE_MAX = 24;
const LABEL_MAX = 60;

const GROUPS: {
  title: string;
  description: string;
  keys: SiteContentKey[];
}[] = [
  {
    title: 'Hero stats',
    description: 'The trust chips beneath the homepage hero search bar.',
    keys: ['hero.hotels', 'hero.cities', 'hero.verified'],
  },
  {
    title: 'The BestAcco difference',
    description:
      'The figures in the "Hotels we would actually stay in" band lower down the page.',
    keys: ['difference.rating', 'difference.hotels', 'difference.cities'],
  },
];

function validate(value: string, label: string): string | null {
  const v = value.trim();
  const l = label.trim();
  if (!v) return 'Value is required.';
  if (v.length > VALUE_MAX) return `Value must be ${VALUE_MAX} characters or fewer.`;
  if (!l) return 'Label is required.';
  if (l.length > LABEL_MAX) return `Label must be ${LABEL_MAX} characters or fewer.`;
  return null;
}

/**
 * Super-admin editor for the public homepage marketing stats (hero chips +
 * BrandDifference figures). Each stat has an editable value + label; the table
 * stays empty until edited, so any untouched stat is tagged "Default" and the
 * storefront falls back to the same defaults shown here.
 */
export function AdminHomepageContentPage() {
  const { data, isLoading, isError, refetch } = useSiteContent();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-500">
        <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
        <span className="ml-3 text-sm">Loading homepage content…</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
        <AlertCircle className="h-8 w-8 text-red-500" aria-hidden="true" />
        <p className="text-sm text-gray-600">Couldn't load homepage content.</p>
        <Button variant="secondary" onClick={() => void refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const storedByKey = new Map<string, SiteContentRow>(
    (data ?? []).map((row) => [row.key, row]),
  );

  return (
    <>
      <PageHeader
        title="Homepage content"
        description="Edit the marketing stats on the public BestAcco homepage. Saving a stat publishes it to visitors immediately."
      />

      <div className="space-y-8">
        {GROUPS.map((group) => (
          <section
            key={group.title}
            className="rounded-xl border border-gray-200 bg-white p-5"
          >
            <h2 className="text-lg font-semibold text-gray-900">{group.title}</h2>
            <p className="mt-1 text-sm text-gray-500">{group.description}</p>

            <div className="mt-5 space-y-4">
              {group.keys.map((key) => {
                const stored = storedByKey.get(key);
                return (
                  <StatRow
                    // Remount after a save (updatedAt changes) so the baseline
                    // resets and the row is no longer "dirty".
                    key={`${key}-${stored?.updatedAt ?? 'default'}`}
                    statKey={key}
                    stored={stored}
                    fallback={SITE_CONTENT_DEFAULTS[key]}
                  />
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </>
  );
}

function StatRow({
  statKey,
  stored,
  fallback,
}: {
  statKey: SiteContentKey;
  stored: SiteContentRow | undefined;
  fallback: SiteContentEntry;
}) {
  const baseValue = stored?.value ?? fallback.value;
  const baseLabel = stored?.label ?? fallback.label;
  const [value, setValue] = useState(baseValue);
  const [label, setLabel] = useState(baseLabel);
  const upsert = useUpsertSiteContent();

  const error = validate(value, label);
  const dirty = value !== baseValue || label !== baseLabel;
  const isDefault = !stored;

  function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (error) return;
    upsert.mutate(
      { key: statKey, value: value.trim(), label: label.trim() },
      {
        onSuccess: () => showToast('Saved · live on storefront'),
        onError: (err) => showToast(extractErrorMessage(err), 'error'),
      },
    );
  }

  function handleReset() {
    setValue(baseValue);
    setLabel(baseLabel);
  }

  return (
    <form
      onSubmit={handleSave}
      className="rounded-lg border border-gray-100 bg-gray-50/60 p-4"
    >
      <div className="mb-3 flex items-center gap-2">
        <code className="rounded bg-gray-200/70 px-1.5 py-0.5 text-xs text-gray-600">
          {statKey}
        </code>
        {isDefault && (
          <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
            Default
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[160px_1fr]">
        <Input
          label="Value"
          value={value}
          maxLength={VALUE_MAX}
          onChange={(e) => setValue(e.target.value)}
          placeholder={fallback.value}
        />
        <Input
          label="Label"
          value={label}
          maxLength={LABEL_MAX}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={fallback.label}
          error={dirty && error ? error : undefined}
        />
      </div>

      <div className="mt-3 flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleReset}
          disabled={!dirty || upsert.isPending}
        >
          <RotateCcw className="mr-1.5 h-4 w-4" aria-hidden="true" />
          Reset
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={!dirty || !!error || upsert.isPending}
        >
          {upsert.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}
